import React, { useContext, useEffect, useState } from 'react';
import { Button } from 'primereact/button';
import { isNullOrUndefined } from '@utils/utils';
import {
  dateFromApiStringInTz,
  dayjsAtStartOfDayInTz,
  dayjsFromApiUtcInTz,
  dayjsFromDateAtMidnightInTz,
  dayjsNowInTz,
  formatDateFromDayjsWithLocale,
  formatTime24hFromDayjs,
  formatTimeIntervalFromStringInTzWithLocale,
  shortDateFormat,
  stringYMDFromDayjs,
  timeSlotProgressTabDtoComparator,
} from '@utils/dateWithTz.tsx';
import { Dayjs } from 'dayjs';
import { Calendar } from 'primereact/calendar';
import { InputMask } from 'primereact/inputmask';
import * as yup from 'yup';
import useValidation from '@hooks/validation-hook/useValidation';
import ValidationDiv from '@utils/validation';
import { LocationTimeslots } from '../types';
import {
  CompanyLocationRampWithOpeningHoursOutDTO,
  EntityTypeEnum,
  ExpeditionStatusEnum,
  LocationsJobProgressOutDTO,
} from '@api/ringil-api.v2';
import ReconfirmQuote from '../../common/ReconfirmQuote';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import CanMakeReservation from '@components/ringil3/Features/Reservation/Helper/CanMakeReservation';
import LinkedResBadge from '@components/ringil3/Badges/LinkedResBadge';
import { useTranslation } from 'react-i18next';
import { ExpeditionDetailHandlerContext } from '@app/pages/carrier/expedition-detail/ExpeditionDetailHandler.tsx';
import { ApiContext } from '@api/api';
import { useCreateTimeslotSidebar } from '@components/ringil3/Features/Reservation/Sidedbars/CreateTimeslotSidebar.tsx';
import HorizontalFieldValue from '@components/obsolete/HorizontalFieldValue.tsx';
import InfoNote from '@components/obsolete/InfoNote/InfoNote.tsx';
import CollapsibleNote from '@components/RingilComponents/RichTextEditor/CollapsibleNote.tsx';
import TimeslotsProgressTitle from '@components/obsolete/TimeslotsProgress/TimeslotsProgressTitle.tsx';
import TimeslotsProgressInput from '@components/obsolete/TimeslotsProgress/TimeslotsProgressInput.tsx';
import TimeslotsProgressInputSelect from '@components/obsolete/TimeslotsProgress/TimeslotsProgressInputSelect.tsx';
import AssignVehicleComponent from '@app/pages/carrier/expedition-detail/AssignVehicle/AssignVehicleComponent.tsx';
import { AdditionalCosts } from '@components/obsolete/AdditionalCosts/AdditionalCosts.tsx';
import PanelWithIcon from '@components/obsolete/PanelWithIcon/PanelWithIcon.tsx';
import Label from '@components/obsolete/Form/Label/Label.tsx';
import CarrierChatComponent from '@app/pages/carrier/Chat/CarrierChatComponent.tsx';
import { formatPrice } from '@utils/priceUtils.ts';
import { getLocationPositionKey } from '@utils/testUtils';
import { toCompactDateTimeRangePart } from '@utils/carrierShipmentTestIds';

interface ComponentProps {
  disabled?: boolean;
}

const TabShipmentAssigned: React.FC<ComponentProps> = ({ disabled }) => {
  const { t } = useTranslation('common');
  const {
    state,
    loadShipmentProgress,
    selectSlot,
    createJobProgressTabForReadyToShipment,
    addCarrierSlot,
    changeSince,
    changeTill,
    changeCustomSlotDate,
    reconfirm,
  } =
    useContext(ExpeditionDetailHandlerContext);
  const { getSharedRampsForCompanyLocation } = useContext(ApiContext);

  const [visibleChatDialog, setVisibleChatDialog] = useState(false);
  const [rampsByLocationId, setRampsByLocationId] = useState<Record<string, CompanyLocationRampWithOpeningHoursOutDTO[]>>({});

  const load = () => {
    loadShipmentProgress(state.header.companyId, state.header.applicationId);
  };

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    if (state.assigningStateData && state.assigningStateData.locations) {
      state.assigningStateData.locations.forEach(location => {
        const companyLocationIdForRampFetching = location.companyLocationId;
        const customerIdAtLocation = location.companyLocationCustomerId;

        if (companyLocationIdForRampFetching && customerIdAtLocation && rampsByLocationId[companyLocationIdForRampFetching] === undefined) {
          setRampsByLocationId(prevRamps => ({ ...prevRamps, [companyLocationIdForRampFetching]: null }));
          getSharedRampsForCompanyLocation(companyLocationIdForRampFetching, customerIdAtLocation, true, ramps => {
            setRampsByLocationId(prevRamps => ({ ...prevRamps, [companyLocationIdForRampFetching]: ramps || [] }));
          });
        }
      });
    }
  }, [state.assigningStateData, getSharedRampsForCompanyLocation]);

  const hasNewSlot = (locationId: string) => {
    const locationSlot = state.createReadyToShipment.locationTimeslotIds.find(t => t.locationId === locationId);
    if (locationSlot) return !isNullOrUndefined(locationSlot.newTimeslot);
    return false;
  };

  const getNewSlot = (locationId: string) => {
    const locationSlot = state.createReadyToShipment.locationTimeslotIds.find(t => t.locationId === locationId);
    return locationSlot.newTimeslot;
  };

  const validationSchema = yup.object().shape({
    carPlate: yup.string().required().min(1),
    dispatcherId: yup.string().required(),
    driver: yup.object().shape({
      id: yup.string().required(),
    }),
    locationTimeslotIds: yup.array().of(
      yup.object().shape({
        locationId: yup.string().defined().min(1),
        selectedSlot: yup.mixed().test({
          name: 'required',
          test: (value: any, testContext) => {
            const v = testContext.parent as LocationTimeslots;
            if (!isNullOrUndefined(v.timeslotId)) return true;
            // TODO: implement  better checks
            if (!isNullOrUndefined(v.newTimeslot) && v.newTimeslot.timeslotSince !== 'Invalid Date') return true;
            return false;
          },
          message: 'required',
          exclusive: true,
        }),
        newSlot: yup.mixed().test({
          name: 'intervals.not.valid',
          message: 'intervals.not.valid',
          test: (value: any, testContext) => {
            const v = testContext.parent as LocationTimeslots;
            if (!isNullOrUndefined(v.timeslotId)) return true;
            // TODO: implement  better checks
            if (!isNullOrUndefined(v.newTimeslot) && v.newTimeslot.timeslotSince < v.newTimeslot.timeslotTill) return true;
            return false;
          },
        }),
      }),
    ),
  });

  const save = () => {
    createJobProgressTabForReadyToShipment(state.header.companyId, state.header.applicationId, {
      carPlate: state.createReadyToShipment.carPlate,
      dispatcherId: state.createReadyToShipment.dispatcherId,
      driverId: state.createReadyToShipment.driver?.id,
      locationTimeslotIds: state.createReadyToShipment.locationTimeslotIds,
    });
  };
  const { validateAndSend, validationErrors } = useValidation(validationSchema, save);
  const { CreateTimeslotSidebar, setCreateTimeslotRequest, createTimeslotRequest } = useCreateTimeslotSidebar();

  const { tr } = useTranslationLgs();

  const { i18n } = useTranslation();

  const computePreferredDate = (
    location: LocationsJobProgressOutDTO,
  ): string => {
    console.log(`TabShipmentAssigned Computing preferred date for location ${location.id} with timezone ${location.timezoneId}`);
    if (location.timeslots.length > 0) {
      const dayjsAtStartOfDay = location.timezoneId != null
        ? dayjsAtStartOfDayInTz(dayjsNowInTz(location.timezoneId), location.timezoneId)
        : dayjsAtStartOfDayInTz(dayjsNowInTz('Europe/Prague'), 'Europe/Prague');

      const firstFutureSlot = location.timeslots
        .filter(slot => {
          const slotDayjs = location.timezoneId != null
            ? dayjsFromApiUtcInTz(slot.utcStart, location.timezoneId)
            : dayjsFromApiUtcInTz(slot.sinceInUTC, 'Europe/Prague');
          return slotDayjs.isAfter(dayjsAtStartOfDay);
        })
        .sort((a, b) => timeSlotProgressTabDtoComparator(a,b, location.timezoneId))[0];

      if (firstFutureSlot) {
        const date = location.timezoneId != null
          ? dayjsFromApiUtcInTz(firstFutureSlot.utcStart, firstFutureSlot.timezoneId)
          : dayjsFromApiUtcInTz(firstFutureSlot.sinceInUTC, 'Europe/Prague');
        return stringYMDFromDayjs(date);
      }
    }

    // today date is good enough
    return new Date().toISOString().split('T')[0];
  };

  const getLocationPrefix = (location: LocationsJobProgressOutDTO, locationIndex: number) =>
    `shipmentProgress-${getLocationPositionKey(location.id, locationIndex, state.assigningStateData.locations.length)}`;

  return (
    <>
      {state.assigningStateData && (
        <>
          <div className="p-d-flex p-grid">
            <div className="p-col-12 p-md-6 p-lg-6 p-mr-6">
              {state.assigningStateData.carrierNote && (
                <HorizontalFieldValue label={tr(`TabShipmentAssigned.carrierNote`, `Carrier note`)}
                                      value={<InfoNote text={<CollapsibleNote html={state.assigningStateData.carrierNote} bgClassName={'bg-[color:var(--info-alert-background-color)]'} />} />}
                                      dataTestId="shipmentProgress-noteForCarrier" />
              )}
              {state.assigningStateData.isReconfirmedNeeded && (
                <div className="p-mb-3">
                  <ReconfirmQuote
                    onReconfirm={() => reconfirm(state.header.companyId, state.header.applicationId, load)}
                    reconfirmedAtUtc={state.assigningStateData.reconfirmedAtUtc}
                    timezoneId={state.assigningStateData.timezoneId}
                    isReconfirmedByCarrier={state.assigningStateData.isReconfirmedByCarrier}
                    disabled={state.header.expeditionStatus === ExpeditionStatusEnum.CANCELED || state.header.isManualExpedition}
                  />
                </div>
              )}
              <h3 className="p-mt-0" style={{ fontWeight: 'normal' }}>
                {t(`TabShipmentAssigned.selectTimeslotsForLoadingAndUnloading`, `Select timeslots for loading and unloading`)}
              </h3>
              {state.assigningStateData.locations.map((location, locationIndex) => {
                const locationPrefix = getLocationPrefix(location, locationIndex);
                return (
                  <div className="timeslotsProgress" key={locationIndex}>
                    <TimeslotsProgressTitle
                      number={`${locationIndex + 1}`}
                      address={`${location.zipCode}, ${location.city}, (${location.country})`}
                      company={location.name}
                      timezoneId={location.timezoneId}
                      dataTestIdLocationName={locationPrefix}
                    />
                    <div className="timeslotsProgress-wrapper">
                      {[...location.timeslots]
                        .sort((a, b) => timeSlotProgressTabDtoComparator(a, b, location.timezoneId))
                        .map((timeslot, timeslotIndex) => {
                          const slotDayJs = location.timezoneId != null
                            ? dayjsFromApiUtcInTz(timeslot.utcStart, location.timezoneId)
                            : dayjsFromApiUtcInTz(timeslot.sinceInUTC, 'Europe/Prague');
                          const slotDate = formatDateFromDayjsWithLocale(slotDayJs);
                          const sinceString = timeslot.timezoneId != null ? timeslot.utcStart : timeslot.sinceInUTC;
                          const tillString = timeslot.timezoneId != null?  timeslot.utcEnd : timeslot.tillInUTC;
                          const timezoneId = timeslot.timezoneId ?? 'Europe/Prague';
                          const slotTime = formatTimeIntervalFromStringInTzWithLocale(timeslot.isNotSpecifiedTime, sinceString, tillString, timezoneId, t);
                          return (
                          <TimeslotsProgressInput
                            key={timeslotIndex}
                            date={slotDate}
                            time={slotTime}
                            checked={state.createReadyToShipment.locationTimeslotIds.some(l => l.locationId == location.id && l.timeslotId == timeslot.id)}
                            onCheck={() => selectSlot(location.id, timeslot.id)}
                            disabled={disabled}
                            dataTestId={`${locationPrefix}-expectedHandling-${toCompactDateTimeRangePart(
                              dayjsFromApiUtcInTz(sinceString, timezoneId).format('YYYY-MM-DD'),
                              dayjsFromApiUtcInTz(sinceString, timezoneId).format('HH:mm'),
                              dayjsFromApiUtcInTz(tillString, timezoneId).format('HH:mm'),
                            )}`}
                          />
                        );
                      })}
                    </div>
                    {hasNewSlot(location.id) && (
                      <>
                        <TimeslotsProgressInputSelect
                          inputName="dataTime1"
                          inputValue="data-time-4"
                          checked={true}
                          dataTestId={`${locationPrefix}-expectedHandling-pickTime`}
                        >
                          <div className="timeslotsProgress-calendar"
                               data-testid={`${locationPrefix}-expectedHandling-pickTime-date`}>
                            <Calendar
                              locale={i18n.language}
                              value={dateFromApiStringInTz(getNewSlot(location.id).utcStart ?? getNewSlot(location.id).timeslotSince, location.timezoneId ?? 'Europe/Prague')}
                              dateFormat={shortDateFormat}
                              onChange={e => {
                                // PrimeReact fires onChange with a null value when the user types an
                                // unparseable/incomplete date. Ignore it instead of formatting null. (Sentry QF.)
                                if (isNullOrUndefined(e.value)) return;
                                const dayjs = dayjsFromDateAtMidnightInTz(e.value as Date, location.timezoneId ?? 'Europe/Prague');
                                changeCustomSlotDate(location.id, dayjs as Dayjs);
                              }}
                            />
                          </div>
                          <div className="timeslotsProgress-input p-fluid"
                               data-testid={`${locationPrefix}-expectedHandling-pickTime-timeFrom`}>
                            <InputMask
                              mask={'99:99'}
                              value={formatTime24hFromDayjs(dayjsFromApiUtcInTz(getNewSlot(location.id).utcStart ?? getNewSlot(location.id).timeslotSince, location.timezoneId ?? 'Europe/Prague'))}
                              onComplete={e => changeSince(location.id, e.value)}
                            />
                          </div>
                          <div className="delimiter"> —</div>
                          <div className="timeslotsProgress-input p-fluid"
                               data-testid={`${locationPrefix}-expectedHandling-pickTime-timeTo`}>
                            <InputMask
                              mask={'99:99'}
                              value={formatTime24hFromDayjs(dayjsFromApiUtcInTz(getNewSlot(location.id).utcEnd ?? getNewSlot(location.id).timeslotTill, location.timezoneId ?? 'Europe/Prague'))}
                              onComplete={e => changeTill(location.id, e.value)}
                            />
                          </div>
                          <ValidationDiv errors={validationErrors}
                                         path={`locationTimeslotIds[${locationIndex}].newSlot`} />
                        </TimeslotsProgressInputSelect>
                      </>
                    )}
                    {!hasNewSlot(location.id) && state.header?.disableCarrierProgressTimeslots !== true && (
                      <Button
                        icon="icon pi pi-plus"
                        className="btn-plus p-mt-2 p-button-outlined"
                        label={t(`TabShipmentAssigned.pickASpecificTime`, `Pick a specific time`)}
                        onClick={() => addCarrierSlot(location.id)}
                        disabled={disabled}
                        data-testid={`${locationPrefix}-expectedHandling-pickTime`}
                      />
                    )}
                    <ValidationDiv errors={validationErrors}
                                   path={`locationTimeslotIds[${locationIndex}].selectedSlot`} />
                    {location.linkedReservation && <LinkedResBadge linkedReservation={location.linkedReservation} />}
                    {isNullOrUndefined(location.linkedReservation) &&
                      Array.isArray(rampsByLocationId[location.companyLocationId]) &&
                      rampsByLocationId[location.companyLocationId].length > 0 && (
                        <CanMakeReservation companyLocationId={location.companyLocationId}>
                          <Button
                            label={tr(`TabShipmentActive2Finish.createReservation`, `Create reservation`)}
                            onClick={e => {
                              setCreateTimeslotRequest({
                                applicationId: state.header.applicationId,
                                companyLocationId: location.companyLocationId,
                                preferredDate: computePreferredDate(location),
                                companyLocationCustomerId: location.companyLocationCustomerId,
                                expeditionLocationId: location.id,
                                expeditionId: state.header.id,
                                timezoneId: rampsByLocationId[location.companyLocationId][0]?.timezoneId,
                                testIdPrefix: locationPrefix,
                              });
                            }}
                            data-testid={`${locationPrefix}-expectedHandling-newReservation`}
                          />
                        </CanMakeReservation>
                      )}
                  </div>
                );
              })}
              <AssignVehicleComponent validationErrors={validationErrors} />
              <div className="p-field p-mb-5">
                <Button
                  label={t(`TabShipmentAssigned.save`, `Save`)}
                  style={{ width: '150px' }}
                  className="p-button-rounded"
                  onClick={e => validateAndSend(state.createReadyToShipment)}
                  disabled={disabled}
                  data-testid="shipmentProgress-save"
                />
              </div>
              <AdditionalCosts applicationId={state.header.applicationId} companyId={state.header.companyId}
                               editable={false} />
            </div>
            <div className="p-col-12 p-md-5">
              <h3 className="p-mt-0" style={{ fontWeight: 'normal' }}>
                {t(`TabShipmentAssigned.customer`, `Customer`)}
              </h3>
              <PanelWithIcon srcIcon="/images/contacts.svg">
                <Label title={state.assigningStateData.customerDetail.name} />
                <div data-testid="shipmentProgress-customerName" style={{ display: 'none' }}>{state.assigningStateData.customerDetail.name}</div>
                <p>{`${state.assigningStateData.customerDetail.streetNr}, ${state.assigningStateData.customerDetail.city}, ${state.assigningStateData.customerDetail.country}`}</p>
              </PanelWithIcon>
              <PanelWithIcon srcIcon="/images/user.svg">
                <p data-testid="shipmentProgress-responsiblePerson">{state.assigningStateData.responsiblePersonName}</p>
                <p className="text-gray5">
                  <small>{t(`TabShipmentAssigned.responsiblePerson`, `responsible person`)}</small>
                </p>
              </PanelWithIcon>
              <PanelWithIcon srcIcon="/images/envelope.svg">
                <Button
                  icon="pi pi-envelope"
                  className="p-button-outlined"
                  label={t(`TabShipmentAssigned.writeMessage`, `Napsat zprávu`)}
                  onClick={() => setVisibleChatDialog(true)}
                  data-testid="shipmentProgress-writeMessage"
                />
              </PanelWithIcon>
              <PanelWithIcon srcIcon="/images/money.svg">
                <p>{t(`TabShipmentAssigned.agreedPrice`, `Agreed price`)}</p>
                <p>
                  <strong data-testid="shipmentProgress-price">{formatPrice(state.assigningStateData.agreedPrice, state.assigningStateData.currency)}</strong>
                </p>
              </PanelWithIcon>
            </div>
          </div>
          <CarrierChatComponent
            companyId={state.header.companyId}
            entityType={EntityTypeEnum.EXPEDITION}
            entityId={state.header.id}
            visible={visibleChatDialog}
            onHide={() => {
              setVisibleChatDialog(false);
            }}
            customerName={state.header.customerName}
          />
          {createTimeslotRequest && (
            <CreateTimeslotSidebar
              onContinue={() => {
                load();
              }}
            />
          )}
        </>
      )}
    </>
  );
};

export default TabShipmentAssigned;
